import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { reportImportErrors } from "@/lib/importUtils";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, FolderInput, Globe, Download } from "lucide-react";
import { exportFilename } from "@/lib/timefmt";
import { SortTh, FilterTh, filterRows, sortRows } from "@/components/tableTools";

const emptyLoc = { name: "", type: "", parent_id: "", lat: "", lng: "", dst: false, pers_selectable: true, task_selectable: true, common: true };
const cellCls = "w-full px-2 py-1.5 rounded bg-slate-950 border border-slate-800 text-xs";
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function LocationsTab() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(emptyLoc);
  const [drafts, setDrafts] = useState({});
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [locTypes, setLocTypes] = useState([]);
  const [ownTypes, setOwnTypes] = useState([]);
  const [commonTypes, setCommonTypes] = useState([]);
  const [sharedTypes, setSharedTypes] = useState([]);
  const [newTypeCommon, setNewTypeCommon] = useState(true);
  const [expFmt, setExpFmt] = useState("csv");
  const [expBusy, setExpBusy] = useState(false);
  const [newType, setNewType] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [filters, setFilters] = useState({ name: "", type: "", parent: "" });
  const [selRows, setSelRows] = useState(new Set());
  const [moveTarget, setMoveTarget] = useState("");
  const [moving, setMoving] = useState(false);

  const loadTypes = () => companyId && api.get(`/location-types?company_id=${companyId}`)
    .then((r) => {
      setLocTypes(r.data.types || []);
      setOwnTypes(r.data.own || r.data.types || []);
      setCommonTypes(r.data.common_own || []);
      setSharedTypes(r.data.shared || []);
    }).catch(() => {});

  const saveTypes = async (types, common) => {
    try {
      const { data } = await api.put(`/admin/companies/${companyId}/location-types`, { types, common_types: common ?? commonTypes });
      setOwnTypes(data.types);
      setCommonTypes(data.common_own ?? common ?? commonTypes);
      loadTypes();
      toast.success("Location types saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleTypeCommon = (t) => (commonTypes.includes(t) ? commonTypes.filter((x) => x !== t) : [...commonTypes, t]);

  const exportLocations = async () => {
    setExpBusy(true);
    try {
      const res = await api.get("/admin/locations/export", { params: { company_id: companyId, fmt: expFmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const cname = (companies.find((c) => c.id === companyId) || {}).name || "locations";
      a.href = url;
      a.download = exportFilename(`locations-${cname}`, expFmt, user);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
    finally { setExpBusy(false); }
  };

  useEffect(() => {
    api.get("/companies").then((r) => { setCompanies(r.data); if (r.data[0]) setCompanyId((p) => p || r.data[0].id); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => companyId && api.get(`/locations?company_id=${companyId}`)
    .then((r) => { setLocations(r.data); setDrafts({}); setSelRows(new Set()); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => { if (companyId) { load(); loadTypes(); } }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const locById = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);
  const locLabel = (l) => (l ? `${l.name}${l.type ? ` — ${l.type}` : ""}` : "");

  const rowOf = (l) => drafts[l.id] || l;
  const setField = (l, k, v) => setDrafts((d) => ({ ...d, [l.id]: { ...(d[l.id] || l), [k]: v } }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      company_id: companyId, name: form.name.trim(), lat: numOrNull(form.lat), lng: numOrNull(form.lng), dst: !!form.dst,
      type: form.type || null, parent_id: form.parent_id || null,
      pers_selectable: !!form.pers_selectable, task_selectable: !!form.task_selectable, common: !!form.common,
    };
    try {
      await api.post("/admin/locations", payload);
      toast.success("Location added");
      setForm(emptyLoc);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveRow = async (l) => {
    const d = drafts[l.id];
    if (!d) return;
    if (!String(d.name).trim()) { toast.error("Name is required"); return; }
    try {
      await api.put(`/admin/locations/${l.id}`, {
        company_id: companyId, name: String(d.name).trim(), lat: numOrNull(d.lat), lng: numOrNull(d.lng), dst: !!d.dst,
        type: d.type || null, parent_id: d.parent_id || null,
        pers_selectable: !!d.pers_selectable, task_selectable: !!d.task_selectable, common: !!d.common,
      });
      toast.success(`Saved "${String(d.name).trim()}"`);
      setDrafts((dr) => { const n = { ...dr }; delete n[l.id]; return n; });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (l) => {
    if (!window.confirm(`Delete location "${l.name}"? Users with this location will be unassigned and child locations moved to top level.`)) return;
    await api.delete(`/admin/locations/${l.id}`);
    toast.success("Location deleted");
    load();
  };

  const toggleRow = (id) => setSelRows((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const bulkMove = async () => {
    if (!selRows.size) return;
    setMoving(true);
    try {
      const { data } = await api.post("/admin/locations/move", { location_ids: [...selRows], parent_id: moveTarget || null });
      toast.success(`Moved ${data.moved} location(s)${moveTarget ? ` under ${locLabel(locById[moveTarget])}` : " to top level"}`);
      setSelRows(new Set());
      setMoveTarget("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMoving(false); }
  };

  const runImport = async () => {
    if (!importText.trim()) { toast.error("Paste one location per line: Name, type (optional), lat, lng, dst"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/locations/import", { company_id: companyId, text: importText });
      toast.success(`Imported ${data.added} location(s)${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      reportImportErrors(data, "locations", user);
      setImportText("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  // Sort/filter on committed values only — an unsaved draft must not move or hide the row until Save.
  const textOf = (l) => ({ name: l.name, type: l.type || "", parent: locLabel(locById[l.parent_id]) });
  const keyOf = (l, k) => {
    if (k === "lat" || k === "lng") return Number(l[k]) || 0;
    if (k === "parent") return locLabel(locById[l.parent_id]).toLowerCase();
    return String(l[k] ?? "").toLowerCase();
  };
  const rows = useMemo(
    () => sortRows(filterRows(locations, textOf, filters), sort, keyOf),
    [locations, filters, sort] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const allVisibleSelected = rows.length > 0 && rows.every((l) => selRows.has(l.id));
  const toggleAllVisible = () => setSelRows((s) => {
    const n = new Set(s);
    if (allVisibleSelected) rows.forEach((l) => n.delete(l.id));
    else rows.forEach((l) => n.add(l.id));
    return n;
  });

  const thCls = "px-3 py-2.5 text-[10px] uppercase tracking-widest text-slate-400 font-mono-num font-medium text-center";

  return (
    <div className="space-y-5">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="locations-company"
          className="w-full md:w-72 px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="locations-card">
        <div className="font-heading text-lg font-semibold mb-1">Locations</div>
        <p className="text-xs text-slate-500 mb-4">
          Edit locations directly in the table and Save each changed row. Locations form a hierarchy — set a <span className="text-slate-400">Parent</span> to nest a location under another. Tick rows to move several locations at once. <span className="text-slate-400">Pers</span> = members can pick it as their home/fatigue location (Profile → Preferences); <span className="text-slate-400">Task</span> = it can be used on a task. Latitude/longitude drive sunrise &amp; sunset on the task timeline (latitude −ve = South, longitude −ve = West); DST adds one hour.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-12 gap-3">
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Name / city</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="location-name"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" placeholder="e.g. Brize Norton" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="location-type"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="">—</option>
              {locTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Parent (optional)</label>
            <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} data-testid="location-parent"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="">Top level</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{locLabel(l)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5" title="Optional — negative = South">Lat (−S, opt)</label>
            <input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="location-lat"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" placeholder="51.75" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5" title="Optional — negative = West">Lng (−W, opt)</label>
            <input type="number" step="0.0001" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="location-lng"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" placeholder="-1.58" />
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-300" title="Daylight saving time">
              <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={form.dst} data-testid="location-dst"
                onChange={(e) => setForm({ ...form, dst: e.target.checked })} />
              DST
            </label>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-300" title="Members can pick this as their home/fatigue location">
              <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={form.pers_selectable} data-testid="location-pers"
                onChange={(e) => setForm({ ...form, pers_selectable: e.target.checked })} />
              Pers
            </label>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-300" title="This location can be used on tasks">
              <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={form.task_selectable} data-testid="location-task"
                onChange={(e) => setForm({ ...form, task_selectable: e.target.checked })} />
              Task
            </label>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-300" title="Common — available to all companies">
              <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={form.common} data-testid="location-common"
                onChange={(e) => setForm({ ...form, common: e.target.checked })} />
              Common
            </label>
          </div>
          <div className="flex items-end">
            <button onClick={submit} data-testid="location-save"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        </div>

        {selRows.size > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 p-3 rounded-md border border-blue-600/40 bg-blue-600/5" data-testid="locations-move-bar">
            <span className="text-xs text-slate-300 font-medium">{selRows.size} selected — move under:</span>
            <select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} data-testid="locations-move-target"
              className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="">Top level (no parent)</option>
              {locations.filter((l) => !selRows.has(l.id)).map((l) => (
                <option key={l.id} value={l.id}>{locLabel(l)}</option>
              ))}
            </select>
            <button onClick={bulkMove} disabled={moving} data-testid="locations-move-apply"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-50 transition-colors">
              <FolderInput className="h-3.5 w-3.5" /> {moving ? "Moving…" : "Move selected"}
            </button>
            <button onClick={() => { setSelRows(new Set()); setMoveTarget(""); }} data-testid="locations-move-clear"
              className="px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs text-slate-300 transition-colors">
              Clear selection
            </button>
          </div>
        )}

        <div className="mt-4 overflow-x-auto rounded-md border border-slate-800">
          <table className="w-full" data-testid="locations-table">
            <thead>
              <tr className="border-b border-slate-800/60 text-left">
                <th className="pl-3 pr-1 py-2.5 w-8">
                  <input type="checkbox" className="accent-blue-600 h-4 w-4 align-middle" checked={allVisibleSelected}
                    onChange={toggleAllVisible} data-testid="locations-select-all" title="Select all visible rows" />
                </th>
                <SortTh label="Name" k="name" sort={sort} setSort={setSort} />
                <SortTh label="Type" k="type" sort={sort} setSort={setSort} />
                <SortTh label="Parent" k="parent" sort={sort} setSort={setSort} />
                <SortTh label="Lat (−S)" k="lat" sort={sort} setSort={setSort} right />
                <SortTh label="Lng (−W)" k="lng" sort={sort} setSort={setSort} right />
                <th className={thCls}>DST</th>
                <th className={thCls} title="Personnel selectable — members can pick it as home/fatigue location">Pers</th>
                <th className={thCls} title="Task selectable — usable as a task location">Task</th>
                <th className={thCls} title="Common — available to all companies">Cmn</th>
                <th className="px-3 py-2.5"></th>
              </tr>
              <tr className="border-b border-slate-800">
                <th></th>
                <FilterTh value={filters.name} onChange={(v) => setFilters((f) => ({ ...f, name: v }))} testId="locations-filter-name" />
                <FilterTh value={filters.type} onChange={(v) => setFilters((f) => ({ ...f, type: v }))} testId="locations-filter-type" />
                <FilterTh value={filters.parent} onChange={(v) => setFilters((f) => ({ ...f, parent: v }))} testId="locations-filter-parent" />
                <th colSpan={7} className="px-2 pb-2 pt-0"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-5 text-sm text-slate-500">No locations match.</td></tr>
              )}
              {rows.map((l) => {
                const r = rowOf(l);
                const dirty = !!drafts[l.id];
                return (
                  <tr key={l.id} className={`border-b border-slate-800/70 ${dirty ? "bg-blue-600/5" : ""} ${selRows.has(l.id) ? "bg-blue-600/10" : ""}`} data-testid={`location-row-${l.id}`}>
                    <td className="pl-3 pr-1 py-1.5">
                      <input type="checkbox" className="accent-blue-600 h-4 w-4 align-middle" checked={selRows.has(l.id)}
                        onChange={() => toggleRow(l.id)} data-testid={`location-select-${l.id}`} />
                    </td>
                    <td className="px-2 py-1.5 min-w-[150px]">
                      <input value={r.name} onChange={(e) => setField(l, "name", e.target.value)} data-testid={`location-name-${l.id}`} className={cellCls} />
                    </td>
                    <td className="px-2 py-1.5 min-w-[120px]">
                      <select value={r.type || ""} onChange={(e) => setField(l, "type", e.target.value)} data-testid={`location-type-${l.id}`} className={cellCls}>
                        <option value="">—</option>
                        {locTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 min-w-[160px]">
                      <select value={r.parent_id || ""} onChange={(e) => setField(l, "parent_id", e.target.value)} data-testid={`location-parent-${l.id}`} className={cellCls}>
                        <option value="">Top level</option>
                        {locations.filter((x) => x.id !== l.id).map((x) => (
                          <option key={x.id} value={x.id}>{locLabel(x)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 w-24">
                      <input type="number" step="0.0001" value={r.lat ?? ""} onChange={(e) => setField(l, "lat", e.target.value)} data-testid={`location-lat-${l.id}`} className={`${cellCls} text-right`} />
                    </td>
                    <td className="px-2 py-1.5 w-24">
                      <input type="number" step="0.0001" value={r.lng ?? ""} onChange={(e) => setField(l, "lng", e.target.value)} data-testid={`location-lng-${l.id}`} className={`${cellCls} text-right`} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={!!r.dst} onChange={(e) => setField(l, "dst", e.target.checked)} data-testid={`location-dst-${l.id}`} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={r.pers_selectable !== false} onChange={(e) => setField(l, "pers_selectable", e.target.checked)} data-testid={`location-pers-${l.id}`} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={r.task_selectable !== false} onChange={(e) => setField(l, "task_selectable", e.target.checked)} data-testid={`location-task-${l.id}`} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={r.common !== false} onChange={(e) => setField(l, "common", e.target.checked)} data-testid={`location-common-${l.id}`} />
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => saveRow(l)} disabled={!dirty} data-testid={`location-save-${l.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs disabled:opacity-30 transition-colors">
                          <Save className="h-3 w-3" /> Save
                        </button>
                        <button onClick={() => del(l)} data-testid={`location-del-${l.id}`}
                          className="p-1.5 rounded-md text-rose-400 hover:bg-rose-900/30 hover:text-rose-300 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="location-types-card">
        <div className="font-heading text-lg font-semibold mb-1">Location Types</div>
        <p className="text-xs text-slate-500 mb-3">Configurable types for locations. Types with a blue globe are <span className="text-slate-400">Common</span> — available to all companies (default Yes). Defaults: City, Base, Airfield, ATC Tower, Range, Airspace.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {ownTypes.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-950 border border-slate-800 text-xs" data-testid={`loc-type-${t}`}>
              {t}
              <button onClick={() => saveTypes(ownTypes, toggleTypeCommon(t))} data-testid={`loc-type-common-${t}`}
                title={commonTypes.includes(t) ? "Common — shared with all companies (click to make company-only)" : "Company-only (click to share with all companies)"}
                className={commonTypes.includes(t) ? "text-sky-400 hover:text-sky-300" : "text-slate-600 hover:text-slate-400"}>
                <Globe className="h-3 w-3" /></button>
              <button onClick={() => saveTypes(ownTypes.filter((x) => x !== t), commonTypes.filter((x) => x !== t))} data-testid={`loc-type-del-${t}`}
                className="text-rose-400 hover:text-rose-300"><Trash2 className="h-3 w-3" /></button>
            </span>
          ))}
          {sharedTypes.filter((t) => !ownTypes.includes(t)).map((t) => (
            <span key={t} title="Common type shared by another company"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-950/60 border border-slate-800/60 text-xs text-slate-500" data-testid={`loc-type-shared-${t}`}>
              <Globe className="h-3 w-3 text-sky-500/70" /> {t}
            </span>
          ))}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <input value={newType} onChange={(e) => setNewType(e.target.value)} data-testid="loc-type-new"
            placeholder="New type…" className="flex-1 min-w-40 px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm" />
          <label className="flex items-center gap-1.5 text-xs text-slate-300" title="Make this type available to all companies">
            <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={newTypeCommon} onChange={(e) => setNewTypeCommon(e.target.checked)} data-testid="loc-type-common-new" />
            Common
          </label>
          <button onClick={() => { if (newType.trim()) { saveTypes([...ownTypes, newType.trim()], newTypeCommon ? [...commonTypes, newType.trim()] : commonTypes.filter((x) => x !== newType.trim())); setNewType(""); } }} data-testid="loc-type-add"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="locations-export-card">
        <div className="font-heading text-lg font-semibold mb-1">Export locations</div>
        <p className="text-xs text-slate-500 mb-3">Admin only. Rows list every location Type first (with its Common flag), then each location with its hierarchy Level (top level = 0), parent, coordinates and selectable flags.</p>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Format</label>
            <select value={expFmt} onChange={(e) => setExpFmt(e.target.value)} data-testid="locations-export-format"
              className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="csv">CSV</option>
              <option value="xlsx">XLSX</option>
            </select>
          </div>
          <button onClick={exportLocations} disabled={expBusy} data-testid="locations-export-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-50 transition-colors">
            <Download className="h-3.5 w-3.5" /> {expBusy ? "Preparing…" : "Download"}
          </button>
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="locations-import-card">
        <div className="font-heading text-lg font-semibold mb-1">Import locations</div>
        <p className="text-xs text-slate-500 mb-3">One per line: <span className="font-mono-num">Name, type (optional), latitude, longitude, dst</span> — type, coordinates and dst are all optional (give both lat &amp; lng or neither; latitude −ve = South, longitude −ve = West). Existing names are skipped. Also accepts the <span className="text-slate-400">Locations export format</span> (Kind,Name,Level,Type,Parent,Lat,Lng,DST,Pers,Task,Common) for round-trip seeding — types are merged in and parent names are resolved into the hierarchy. Set parents in the table after importing simple-format rows.</p>
        <textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={4} data-testid="locations-import-text"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm font-mono-num"
          placeholder={"Brize Norton, Base, 51.7499, -1.5836, yes\nAmberley, -27.6406, 152.7119, no"} />
        <button onClick={runImport} disabled={importing} data-testid="locations-import-btn"
          className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-50">
          <Upload className="h-4 w-4" /> {importing ? "Importing…" : "Import"}
        </button>
      </div>
    </div>
  );
}
